async function readBlobAsUtf8(body) {
  if (body && typeof body.text === "function") return body.text();
  if (body && typeof body.arrayBuffer === "function") {
    const ab = await body.arrayBuffer();
    return new TextDecoder("utf-8").decode(ab);
  }
  if (typeof Buffer !== "undefined" && Buffer.isBuffer(body)) {
    return body.toString("utf-8");
  }
  return null;
}

/**
 * True when the failure should show the same UX as an expired or invalid public link
 * (404/401/403/410 or known public `detail.code` values).
 * @param {unknown} err
 * @returns {boolean}
 */
export function isPublicDocumentLinkInvalid(err) {
  const status = err?.response?.status;
  if (status === 404 || status === 401 || status === 403 || status === 410) return true;
  const code = err?.response?.data?.detail?.code;
  if (typeof code === "string") {
    if (code === "public_resource_unavailable" || code === "public_link_expired") return true;
  }
  return false;
}

/**
 * When `responseType: "blob"`, Axios leaves error bodies as Blob; FastAPI JSON
 * (e.g. 503 `detail.code`) is then invisible to `getApiErrorMessage`. Await this
 * in a catch block so `err.response.data` becomes parsed JSON when Content-Type
 * is JSON.
 * @param {unknown} err
 * @returns {Promise<unknown>} same err (mutates response.data when applicable)
 */
export async function hydrateAxiosErrorFromBlobResponse(err) {
  const res = err?.response;
  const body = res?.data;
  if (!res || !body) return err;
  const ct = res.headers?.["content-type"] || res.headers?.["Content-Type"] || "";
  if (!String(ct).includes("application/json")) return err;
  const text = await readBlobAsUtf8(body);
  if (text == null) return err;
  try {
    res.data = JSON.parse(text);
  } catch {
    /* leave body as-is */
  }
  return err;
}

/**
 * Normalizes FastAPI / Axios error payloads for user-facing messages.
 * @param {unknown} err - Typically an Axios error
 * @param {string} fallback - i18n string when no safe detail exists
 * @param {Record<string, string>} [codeMap] - maps `detail.code` (public API) to translated copy
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback, codeMap) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && typeof d.code === "string") {
    const key = d.code.trim();
    const mapped = codeMap && typeof codeMap === "object" ? codeMap[key] : null;
    if (typeof mapped === "string" && mapped.trim()) return mapped.trim();
    if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
  }
  if (typeof d === "string" && d.trim()) return d.trim();
  if (Array.isArray(d)) {
    const parts = d
      .map((x) => (x && typeof x === "object" && typeof x.msg === "string" ? x.msg : null))
      .filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  if (d && typeof d === "object" && typeof d.msg === "string" && d.msg.trim()) return d.msg.trim();
  return typeof fallback === "string" ? fallback : "Error";
}
